import React, { useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
// Remove Slider import - no longer needed

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';
import { AppPromoBanner, NamedLink } from '../../../../components';
import { useConfiguration } from '../../../../context/configurationContext';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const config = useConfiguration();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
    sectionName,
    isSectionTitleH1 = false,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  // The landing hero renders its own custom layout, but it still needs to expose a single
  // <h1> for search engines and screen readers. Use the section title from Console when it
  // has been filled in, otherwise fall back to a screen-reader-only heading.
  const hasHeroTitle = hasDataInFields([title], fieldOptions);

  // Get categories from config
  const categories = config.categoryConfiguration.categories;

  // Custom draggable slider state
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [dragThreshold] = useState(10); // Minimum distance to consider as dragging
  const [hasMoved, setHasMoved] = useState(false);

  // Check if shadows should be visible
  const checkScrollPosition = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      const hasMoreContentRight = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding errors
      const hasMoreContentLeft = scrollLeft > 1; // Show left shadow when scrolled away from start
      setShowRightShadow(hasMoreContentRight);
      setShowLeftShadow(hasMoreContentLeft);
    }
  };

  // Check scroll position on mount and when categories change
  useEffect(() => {
    checkScrollPosition();
  }, [categories]);

  // Handle scroll events to update shadow visibility
  const handleScroll = () => {
    checkScrollPosition();
  };

  // Handle mouse/touch events for dragging
  const handleMouseDown = e => {
    // Don't prevent default immediately - let clicks work
    setIsDragging(true);
    setHasMoved(false);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    // Add dragging class to disable interactions
    if (sliderRef.current) {
      sliderRef.current.classList.add(css.dragging);
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    // Remove dragging class
    if (sliderRef.current) {
      sliderRef.current.classList.remove(css.dragging);
    }
  };

  const handleMouseUp = e => {
    // Only prevent the link click if we were actually dragging
    if (isDragging && hasMoved) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
    setHasMoved(false);
    // Remove dragging class
    if (sliderRef.current) {
      sliderRef.current.classList.remove(css.dragging);
    }
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    const x = e.pageX - sliderRef.current.offsetLeft;
    const distance = Math.abs(x - startX);

    // Only start dragging if we've moved beyond the threshold
    if (distance > dragThreshold) {
      setHasMoved(true);
      e.preventDefault();
      const walk = (x - startX) * 1.5; // Reduced multiplier for smoother scrolling
      sliderRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  // Touch events for mobile
  const handleTouchStart = e => {
    // Don't prevent default immediately - let the touch work for clicks
    setIsDragging(true);
    setHasMoved(false);
    setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    // Add dragging class to disable interactions
    if (sliderRef.current) {
      sliderRef.current.classList.add(css.dragging);
    }
  };

  const handleTouchMove = e => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
    const distance = Math.abs(x - startX);

    // Only start dragging if we've moved beyond the threshold
    if (distance > dragThreshold) {
      setHasMoved(true);
      e.preventDefault();
      const walk = (x - startX) * 1.5; // Reduced multiplier for smoother scrolling
      sliderRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleTouchEnd = e => {
    // Only prevent the link click if we were actually dragging
    if (isDragging && hasMoved) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
    setHasMoved(false);
    // Remove dragging class
    if (sliderRef.current) {
      sliderRef.current.classList.remove(css.dragging);
    }
  };

  // Handle link clicks - only navigate if we weren't dragging
  const handleLinkClick = e => {
    if (isDragging && hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    // Allow normal link behavior if not dragging
    return true;
  };

  // Remove sliderSettings - no longer needed

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(
        rootClassName || css.root,
        sectionId === 'hero-landing-page' ? css.heroLandingPage : ''
      )}
      appearance={appearance}
      options={fieldOptions}
    >
      {sectionId === 'hero-landing-page' ? (
        <>
          {hasHeroTitle ? (
            <Field data={title} className={defaultClasses.title} as="h1" options={fieldOptions} />
          ) : (
            <h1 className={css.srOnlyHeading}>Buy and sell secondhand books in the UK</h1>
          )}
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <div className={css.categorySlider}>
            <div
              ref={sliderRef}
              className={css.draggableSlider}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onScroll={handleScroll}
            >
              {categories.map(category => (
                <NamedLink
                  key={category.id}
                  className={css.categoryItem}
                  name="SearchPage"
                  to={{
                    search: `?pub_categoryLevel1=${category.id}`,
                  }}
                  onClick={handleLinkClick}
                >
                  <span className={css.categoryLabel}>{category.name}</span>
                </NamedLink>
              ))}
            </div>
            {showRightShadow && <div className={`${css.rightShadow} ${css.visible}`}></div>}
            {showLeftShadow && <div className={`${css.leftShadow} ${css.visible}`}></div>}
          </div>
          {/* Renders nothing unless REACT_APP_APP_PROMO_BANNER is switched on. */}
          <AppPromoBanner className={css.appPromo} />
          <div className={css.heroContainer}>
            <div className={css.heroContent}>
              <h4>Make space on your shelves</h4>
              <NamedLink name="NewListingPage" className={css.heroCta}>
                Sell Books
              </NamedLink>
            </div>
            <div className={css.heroContent}>
              <h4>Find your next read</h4>
              <NamedLink name="SearchPage" className={css.heroCta}>
                Buy Books
              </NamedLink>
            </div>
          </div>
        </>
      ) : hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field
            data={title}
            className={defaultClasses.title}
            as={isSectionTitleH1 ? 'h1' : undefined}
            options={fieldOptions}
          />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHero;
